// Desktop header navigation built from the shared navigation structure.

import React, { useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '@/utilities/cn';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../ui/navigation-menu';
import { analyticsService } from '../../services/analyticsService';
import NavItemLabel from './NavItemLabel';
import { getDesktopNavSections } from '@/app/routing/navSections';
import { isNavPathActive } from '@/app/routing/getFrontendRoutes';

type HeaderNavProps = {
  /** Retained for call sites; chrome is unified (dark shell or parent light beat scope). */
  tone?: 'home' | 'solid';
};

const HeaderNav: React.FC<HeaderNavProps> = () => {
  const location = useLocation();
  const sections = useMemo(() => getDesktopNavSections(), []);
  const [openSection, setOpenSection] = useState('');
  const previousOpenSectionRef = useRef('');

  /** Populated dropdowns inherit Header row: `.dark` tokens or `getHomeBeatHeaderScopeStyle` on scroll beats. */
  const externalIconClassName = 'text-muted-foreground';

  const trackClick = (
    label: string,
    sectionKey: string,
    path: string,
    external?: boolean
  ) => {
    analyticsService.trackNavigationItemClick(label, {
      surface: 'site_header',
      menu_name: 'desktop_header',
      nav_section: sectionKey,
      nav_source: 'desktop',
      navigation_target: path,
      link_type: external ? 'external' : 'internal',
      source_path: location.pathname,
    });
  };

  const handleOpenSectionChange = (nextSection: string) => {
    const previousSection = previousOpenSectionRef.current;

    if (previousSection && previousSection !== nextSection) {
      analyticsService.trackNavigationMenuToggle(previousSection, 'closed', {
        surface: 'site_header',
        menu_name: 'desktop_header',
        nav_section: previousSection,
        nav_source: 'desktop',
        source_path: location.pathname,
      });
    }

    if (nextSection && previousSection !== nextSection) {
      analyticsService.trackNavigationMenuToggle(nextSection, 'open', {
        surface: 'site_header',
        menu_name: 'desktop_header',
        nav_section: nextSection,
        nav_source: 'desktop',
        source_path: location.pathname,
      });
    }

    previousOpenSectionRef.current = nextSection;
    setOpenSection(nextSection);
  };

  return (
    <NavigationMenu
      value={openSection}
      onValueChange={handleOpenSectionChange}
      viewport={false}
      className="hidden flex-none items-center justify-end lg:flex"
    >
      <NavigationMenuList className="justify-end gap-2 xl:gap-3">
        {sections.map((section) => {
          const isSectionActive = section.items.some(
            (item) =>
              item.kind === 'link' &&
              !item.external &&
              isNavPathActive(location.pathname, item.path)
          );

          return (
            <NavigationMenuItem key={section.key} value={section.key}>
              <NavigationMenuTrigger
                className={cn(
                  navigationMenuTriggerStyle(),
                  'h-12 rounded-2xl border border-transparent px-4 text-lg font-semibold tracking-tight xl:h-[3.25rem] xl:px-5 xl:text-xl [&_svg]:size-5',
                  'text-foreground hover:bg-accent/70 focus:bg-accent/70 data-open:border-border data-open:bg-background/72 data-open:text-foreground data-open:backdrop-blur-md [&_svg]:text-muted-foreground',
                  isSectionActive && 'text-primary [&_svg]:text-primary'
                )}
              >
                {section.title}
              </NavigationMenuTrigger>
              <NavigationMenuContent
                className={cn(
                  'absolute left-auto right-0 top-full mt-3 w-max min-w-64 max-w-[min(26rem,calc(100vw-2rem))] origin-top-right p-2 backdrop-blur-xl'
                )}
              >
                <ul className="flex min-w-full flex-col gap-1">
                  {section.items.map((item) => {
                    if (item.kind !== 'link') {
                      return null;
                    }

                    const isActive =
                      !item.external &&
                      isNavPathActive(location.pathname, item.path);
                    const isMailto = item.path.startsWith('mailto:');

                    return (
                      <li key={item.path}>
                        <NavigationMenuLink
                          asChild
                          className={cn(
                            'group/nav-item min-h-12 w-full items-start rounded-xl px-4 py-3 text-lg font-medium leading-tight xl:text-xl',
                            'text-popover-foreground hover:bg-accent focus:bg-accent',
                            isActive &&
                              'bg-primary/14 text-primary hover:bg-primary/14 focus:bg-primary/14'
                          )}
                        >
                          {item.external ? (
                            <a
                              href={item.path}
                              target={isMailto ? undefined : '_blank'}
                              rel={isMailto ? undefined : 'noopener noreferrer'}
                              onClick={() =>
                                trackClick(
                                  item.label,
                                  section.key,
                                  item.path,
                                  item.external
                                )
                              }
                            >
                              <NavItemLabel
                                label={item.label}
                                external
                                iconClassName={externalIconClassName}
                              />
                            </a>
                          ) : (
                            <Link
                              to={item.path}
                              onClick={() =>
                                trackClick(
                                  item.label,
                                  section.key,
                                  item.path,
                                  item.external
                                )
                              }
                              aria-current={isActive ? 'page' : undefined}
                            >
                              <NavItemLabel label={item.label} />
                            </Link>
                          )}
                        </NavigationMenuLink>
                      </li>
                    );
                  })}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default HeaderNav;
